import React, { useState, useRef } from 'react';
import { 
  Upload,
  FileText,
  FileUp,
  FileDown,
  FileCheck,
  FileX,
  Eye,
  Download,
  Trash2,
  RefreshCw,
  Search,
  Filter,
  Plus,
  Edit,
  Copy,
  Share,
  Bookmark,
  Star,
  MoreHorizontal,
  AlertCircle,
  CheckCircle,
  Clock,
  Brain,
  Database,
  Layers,
  Settings,
  BarChart3,
  PieChart,
  Activity,
  TrendingUp,
  TrendingDown,
  Target,
  Shield,
  Lock,
  Key,
  Fingerprint,
  Globe,
  Users,
  Building,
  Calendar,
  DollarSign,
  Tag,
  Flag,
  Info,
  ExternalLink,
  Archive,
  Send,
  MessageSquare,
  Phone,
  Mail,
  MapPin,
  User,
  Save,
  X,
  CheckSquare,
  Square,
  ArrowUpRight,
  ArrowDownRight,
  ArrowLeft,
  Home,
  Wrench,
  FileBarChart,
  Cog,
  Link,
  Zap,
  Mic,
  Languages,
  Users2,
  Calculator,
  Receipt,
  CreditCard,
  Monitor,
  Tablet,
  Smartphone,
  Lightbulb,
  Cpu,
  Network,
  AlertTriangle,
  XCircle,
  Heart,
  ThumbsUp,
  ThumbsDown
} from 'lucide-react';
import DataTable from '../common/DataTable';
import ActionMenu from '../common/ActionMenu';
import ConfirmationModal from '../common/ConfirmationModal';
import AIService from '../../services/aiService';
import PageBreadcrumb from '../common/PageBreadcrumb';
import PageHeader from '../common/PageHeader';

const DocumentIngestion = () => {
  const [activeTab, setActiveTab] = useState('upload');
  const [isUploading, setIsUploading] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterType, setFilterType] = useState('all');
  const fileInputRef = useRef(null);

  // Mock data for document ingestion
  const [documentData, setDocumentData] = useState([
    {
      id: 'DOC-001',
      fileName: 'Service_Level_Agreement_IT.pdf',
      originalName: 'Service Level Agreement - IT Services.pdf',
      fileType: 'PDF',
      fileSize: '2.4 MB',
      status: 'Processed',
      processingStatus: 'Completed',
      uploadDate: '2024-02-16T10:30:00Z',
      processedDate: '2024-02-16T10:32:00Z',
      confidence: 94,
      extractedData: {
        contractType: 'Service Level Agreement',
        parties: ['Company A', 'IT Services Provider'],
        startDate: '2024-01-01',
        endDate: '2024-12-31',
        value: '$150,000',
        keyTerms: ['99.9% uptime', '24/7 support', 'Monthly reporting']
      },
      ocrText: 'This Service Level Agreement (SLA) defines the performance standards...',
      metadata: {
        pageCount: 12,
        language: 'English',
        documentType: 'Contract',
        category: 'Service Agreement'
      },
      isActive: true,
      tags: ['sla', 'it-services', 'contract'],
      createdBy: 'AI Document Processor',
      department: 'Legal',
      processingTime: '2.1s',
      isBookmarked: true,
      isStarred: false,
      viewCount: 45,
      downloadCount: 8,
      errorCount: 0,
      warningCount: 1
    },
    {
      id: 'DOC-002',
      fileName: 'Asset_Inventory_Report.xlsx',
      originalName: 'Asset Inventory Report Q1 2024.xlsx',
      fileType: 'Excel',
      fileSize: '1.8 MB',
      status: 'Processing',
      processingStatus: 'In Progress',
      uploadDate: '2024-02-16T11:15:00Z',
      processedDate: null,
      confidence: 0,
      extractedData: null,
      ocrText: null,
      metadata: {
        pageCount: 1,
        language: 'English',
        documentType: 'Spreadsheet',
        category: 'Asset Report'
      },
      isActive: true,
      tags: ['assets', 'inventory', 'report'],
      createdBy: 'AI Document Processor',
      department: 'Finance',
      processingTime: null,
      isBookmarked: false,
      isStarred: true,
      viewCount: 12,
      downloadCount: 3,
      errorCount: 0,
      warningCount: 0
    },
    {
      id: 'DOC-003',
      fileName: 'Compliance_Audit_Report.docx',
      originalName: 'Compliance Audit Report 2024.docx',
      fileType: 'Word',
      fileSize: '3.2 MB',
      status: 'Error',
      processingStatus: 'Failed',
      uploadDate: '2024-02-16T09:45:00Z',
      processedDate: '2024-02-16T09:47:00Z',
      confidence: 0,
      extractedData: null,
      ocrText: null,
      metadata: {
        pageCount: 25,
        language: 'English',
        documentType: 'Report',
        category: 'Compliance'
      },
      isActive: true,
      tags: ['compliance', 'audit', 'report'],
      createdBy: 'AI Document Processor',
      department: 'Legal',
      processingTime: '2.0s',
      isBookmarked: false,
      isStarred: false,
      viewCount: 8,
      downloadCount: 1,
      errorCount: 3,
      warningCount: 2
    }
  ]);

  // Calculate statistics
  const stats = {
    totalDocuments: documentData.length,
    processedDocuments: documentData.filter(d => d.status === 'Processed').length,
    processingDocuments: documentData.filter(d => d.status === 'Processing').length,
    errorDocuments: documentData.filter(d => d.status === 'Error').length,
    averageConfidence: documentData.filter(d => d.confidence > 0).reduce((sum, d) => sum + d.confidence, 0) / documentData.filter(d => d.confidence > 0).length || 0,
    totalFileSize: documentData.reduce((sum, d) => sum + parseFloat(d.fileSize.replace(/[^\d.]/g, '')), 0),
    totalPages: documentData.reduce((sum, d) => sum + d.metadata.pageCount, 0)
  };

  // Filtered data
  const filteredDocuments = documentData.filter(doc => {
    const matchesSearch = searchTerm === '' || 
      doc.originalName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      doc.fileType.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = filterStatus === 'all' || doc.status === filterStatus;
    const matchesType = filterType === 'all' || doc.fileType === filterType;
    
    return matchesSearch && matchesStatus && matchesType;
  });

  const tabs = [
    { id: 'upload', label: 'Upload Documents' },
    { id: 'processing', label: 'Processing Queue' },
    { id: 'extracted', label: 'Extracted Data' },
    { id: 'analytics', label: 'Analytics' }
  ];

  // Define columns for DataTable
  const columns = [
    {
      key: 'originalName',
      label: 'Document',
      width: '300px',
      render: (value, row) => (
        <div>
          <div style={{ fontWeight: '500', color: '#1f2937', marginBottom: '4px' }}>{value}</div>
          <div style={{ fontSize: '12px', color: '#6b7280' }}>
            {row.fileType} • {row.fileSize} • {row.metadata.pageCount} pages
          </div>
        </div>
      )
    },
    {
      key: 'status',
      label: 'Status',
      width: '120px',
      render: (status, row) => {
        const statusConfig = {
          'Processed': { color: '#10b981', bg: '#d1fae5', icon: <CheckCircle size={14} /> },
          'Processing': { color: '#f59e0b', bg: '#fef3c7', icon: <RefreshCw size={14} /> },
          'Error': { color: '#ef4444', bg: '#fee2e2', icon: <XCircle size={14} /> }
        };
        const config = statusConfig[status] || statusConfig['Processing'];
        
        return (
          <span style={{
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            padding: '6px 10px',
            borderRadius: '6px',
            fontSize: '12px',
            fontWeight: '500',
            backgroundColor: config.bg,
            color: config.color,
            width: 'fit-content'
          }}>
            {config.icon}
            {status}
          </span>
        );
      }
    },
    {
      key: 'confidence',
      label: 'Confidence',
      width: '120px',
      render: (confidence) => {
        if (confidence === 0) return <span style={{ color: '#6b7280' }}>-</span>;
        
        const getConfidenceColor = (confidence) => {
          if (confidence >= 90) return '#10b981';
          if (confidence >= 70) return '#f59e0b';
          return '#ef4444';
        };
        
        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <div style={{
              width: '60px',
              height: '6px',
              backgroundColor: '#e5e7eb',
              borderRadius: '3px',
              overflow: 'hidden'
            }}>
              <div style={{
                width: `${confidence}%`,
                height: '100%',
                backgroundColor: getConfidenceColor(confidence),
                transition: 'width 0.3s ease'
              }} />
            </div>
            <span style={{ fontSize: '12px', fontWeight: '500', color: getConfidenceColor(confidence) }}>
              {confidence}%
            </span>
          </div>
        );
      }
    },
    {
      key: 'uploadDate',
      label: 'Uploaded',
      width: '120px',
      render: (date) => new Date(date).toLocaleDateString()
    }
  ];

  // Handler functions
  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFiles(e.dataTransfer.files);
    }
  };

  const handleFileInput = (e) => {
    if (e.target.files && e.target.files[0]) {
      handleFiles(e.target.files);
    }
  };

  const handleFiles = async (files) => {
    setIsUploading(true);
    try {
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        const newDocument = {
          id: `DOC-${Date.now()}-${i}`,
          fileName: file.name,
          originalName: file.name,
          fileType: file.name.split('.').pop().toUpperCase(),
          fileSize: (file.size / 1024 / 1024).toFixed(1) + ' MB',
          status: 'Processing',
          processingStatus: 'In Progress',
          uploadDate: new Date().toISOString(),
          processedDate: null,
          confidence: 0,
          extractedData: null,
          ocrText: null,
          metadata: {
            pageCount: 1,
            language: 'English',
            documentType: 'Unknown',
            category: 'General'
          },
          isActive: true,
          tags: [],
          createdBy: 'AI Document Processor',
          department: 'General',
          processingTime: null,
          isBookmarked: false,
          isStarred: false,
          viewCount: 0,
          downloadCount: 0,
          errorCount: 0,
          warningCount: 0
        };
        
        setDocumentData(prev => [newDocument, ...prev]);
        
        // Simulate document processing
        setTimeout(() => {
          processDocument(newDocument.id);
        }, 2000);
      }
    } catch (error) {
      console.error('Error uploading files:', error);
    } finally {
      setIsUploading(false);
    }
  };

  const processDocument = async (documentId) => {
    setIsProcessing(true);
    try {
      // Call AI service for document processing
      const response = await AIService.processDocument(documentId, {
        extractText: true,
        extractData: true,
        analyzeContent: true
      });
      
      if (response.success) {
        setDocumentData(prev => prev.map(doc => 
          doc.id === documentId 
            ? { 
                ...doc, 
                status: 'Processed',
                processingStatus: 'Completed',
                processedDate: new Date().toISOString(),
                confidence: response.confidence || 85,
                extractedData: response.extractedData,
                ocrText: response.ocrText,
                metadata: response.metadata,
                processingTime: response.processingTime || '2.1s'
              }
            : doc
        ));
      }
    } catch (error) {
      console.error('Error processing document:', error);
      setDocumentData(prev => prev.map(doc => 
        doc.id === documentId 
          ? { 
              ...doc, 
              status: 'Error',
              processingStatus: 'Failed',
              processedDate: new Date().toISOString(),
              errorCount: 1
            }
          : doc
      ));
    } finally {
      setIsProcessing(false);
    }
  };

  const handleView = (document) => {
    //console.log('Viewing document:', document.originalName);
  };

  const handleDownload = (document) => {
    //console.log('Downloading document:', document.originalName);
  };

  const handleDelete = (document) => {
    setSelectedItem(document);
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    setIsDeleting(true);
    try {
      setDocumentData(prev => prev.filter(doc => doc.id !== selectedItem.id));
      setShowDeleteModal(false);
      setSelectedItem(null);
    } catch (error) {
      console.error('Error deleting document:', error);
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <div className="page-container">
      <PageBreadcrumb />
      <PageHeader 
        title="Document Ingestion"
        description="AI-powered document processing, OCR, and intelligent data extraction"
      />

      {/* Tabbed Navigation */}
      <div className="page-tabs">
        <div className="page-tabs-list">
          {tabs.map(tab => (
            <div key={tab.id} className="page-tab-item">
              <button
                className={`page-tab-link ${activeTab === tab.id ? 'active' : ''}`}
                onClick={() => setActiveTab(tab.id)}
              >
                {tab.id === 'upload' && <Upload size={18} className="page-tab-icon" />}
                {tab.id === 'processing' && <RefreshCw size={18} className="page-tab-icon" />}
                {tab.id === 'extracted' && <FileCheck size={18} className="page-tab-icon" />}
                {tab.id === 'analytics' && <BarChart3 size={18} className="page-tab-icon" />}
                {tab.label}
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* Stats Cards */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
        gap: '24px',
        marginBottom: '32px'
      }}>
        <div style={{
          backgroundColor: 'white',
          borderRadius: '12px',
          padding: '20px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          border: '1px solid #e5e7eb'
        }}>
          <div>
            <p style={{ fontSize: '14px', color: '#6b7280', margin: '0 0 8px 0' }}>Total Documents</p>
            <h2 style={{ fontSize: '28px', fontWeight: '700', color: '#1f2937', margin: 0 }}>{stats.totalDocuments}</h2>
          </div>
          <div style={{
            width: '48px',
            height: '48px',
            borderRadius: '12px',
            backgroundColor: 'var(--primary-color)20',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'var(--primary-color)'
          }}>
            <FileText size={20} />
          </div>
        </div>

        <div style={{
          backgroundColor: 'white',
          borderRadius: '12px',
          padding: '20px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          border: '1px solid #e5e7eb'
        }}>
          <div>
            <p style={{ fontSize: '14px', color: '#6b7280', margin: '0 0 8px 0' }}>Processed</p>
            <h2 style={{ fontSize: '28px', fontWeight: '700', color: '#1f2937', margin: 0 }}>{stats.processedDocuments}</h2>
          </div>
          <div style={{
            width: '48px',
            height: '48px',
            borderRadius: '12px',
            backgroundColor: '#10b98120',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#10b981'
          }}>
            <CheckCircle size={20} />
          </div>
        </div>

        <div style={{
          backgroundColor: 'white',
          borderRadius: '12px',
          padding: '20px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          border: '1px solid #e5e7eb'
        }}>
          <div>
            <p style={{ fontSize: '14px', color: '#6b7280', margin: '0 0 8px 0' }}>Avg. Confidence</p>
            <h2 style={{ fontSize: '28px', fontWeight: '700', color: '#1f2937', margin: 0 }}>{Math.round(stats.averageConfidence)}%</h2>
          </div>
          <div style={{
            width: '48px',
            height: '48px',
            borderRadius: '12px',
            backgroundColor: '#f59e0b20',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#f59e0b'
          }}>
            <Target size={20} />
          </div>
        </div>

        <div style={{
          backgroundColor: 'white',
          borderRadius: '12px',
          padding: '20px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          border: '1px solid #e5e7eb'
        }}>
          <div>
            <p style={{ fontSize: '14px', color: '#6b7280', margin: '0 0 8px 0' }}>Total Pages</p>
            <h2 style={{ fontSize: '28px', fontWeight: '700', color: '#1f2937', margin: 0 }}>{stats.totalPages}</h2>
          </div>
          <div style={{
            width: '48px',
            height: '48px',
            borderRadius: '12px',
            backgroundColor: '#8b5cf620',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#8b5cf6'
          }}>
            <Layers size={20} />
          </div>
        </div>
      </div>

      {/* Upload Documents Tab */}
      {activeTab === 'upload' && (
        <div>
          {/* File Upload Area */}
          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: '24px',
            marginBottom: '24px',
            boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
            border: '1px solid #e5e7eb'
          }}>
            <h3 style={{
              fontSize: '18px',
              fontWeight: '600',
              color: '#1f2937',
              margin: '0 0 20px 0',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              <Upload size={20} style={{ color: 'var(--primary-color)' }} />
              Upload Documents
            </h3>
            
            <div
              style={{
                border: `2px dashed ${dragActive ? 'var(--primary-color)' : '#d1d5db'}`,
                borderRadius: '12px',
                padding: '40px',
                textAlign: 'center',
                backgroundColor: dragActive ? '#f0f9ff' : '#f9fafb',
                transition: 'all 0.2s ease',
                cursor: 'pointer'
              }}
              onDragEnter={handleDrag}
              onDragLeave={handleDrag}
              onDragOver={handleDrag}
              onDrop={handleDrop}
              onClick={() => fileInputRef.current?.click()}
            >
              <input
                ref={fileInputRef}
                type="file"
                multiple
                accept=".pdf,.doc,.docx,.xls,.xlsx,.txt,.jpg,.jpeg,.png"
                onChange={handleFileInput}
                style={{ display: 'none' }}
              />
              
              <div style={{
                width: '64px',
                height: '64px',
                borderRadius: '50%',
                backgroundColor: dragActive ? 'var(--primary-color)20' : '#e5e7eb',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 16px',
                transition: 'all 0.2s ease'
              }}>
                <Upload size={32} style={{ color: dragActive ? 'var(--primary-color)' : '#6b7280' }} />
              </div>
              
              <h4 style={{
                fontSize: '18px',
                fontWeight: '600',
                color: '#1f2937',
                margin: '0 0 8px 0'
              }}>
                {dragActive ? 'Drop files here' : 'Drag & drop files here'}
              </h4>
              
              <p style={{
                fontSize: '14px',
                color: '#6b7280',
                margin: '0 0 16px 0'
              }}>
                or click to browse files
              </p>
              
              <div style={{
                fontSize: '12px',
                color: '#9ca3af',
                margin: '0 0 16px 0'
              }}>
                Supported formats: PDF, DOC, DOCX, XLS, XLSX, TXT, JPG, PNG
              </div>
              
              <button
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '10px 20px',
                  backgroundColor: 'var(--primary-color)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: 'pointer',
                  fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif'
                }}
              >
                <Plus size={16} />
                Select Files
              </button>
            </div>
          </div>

          {/* Upload Progress */}
          {isUploading && (
            <div style={{
              backgroundColor: 'white',
              borderRadius: '12px',
              padding: '20px',
              marginBottom: '24px',
              boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
              border: '1px solid #e5e7eb'
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                marginBottom: '16px'
              }}>
                <RefreshCw size={20} style={{ color: 'var(--primary-color)', animation: 'spin 1s linear infinite' }} />
                <h4 style={{
                  fontSize: '16px',
                  fontWeight: '600',
                  color: '#1f2937',
                  margin: 0
                }}>
                  Uploading and Processing Documents...
                </h4>
              </div>
              <div style={{
                width: '100%',
                height: '8px',
                backgroundColor: '#e5e7eb',
                borderRadius: '4px',
                overflow: 'hidden'
              }}>
                <div style={{
                  width: '60%',
                  height: '100%',
                  backgroundColor: 'var(--primary-color)',
                  borderRadius: '4px',
                  transition: 'width 0.3s ease'
                }} />
              </div>
            </div>
          )}
        </div>
      )}

      {/* Processing Queue Tab */}
      {activeTab === 'processing' && (
        <div>
          {/* Processing Controls */}
          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: '24px',
            marginBottom: '24px',
            boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
            border: '1px solid #e5e7eb'
          }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '20px'
            }}>
              <h3 style={{
                fontSize: '18px',
                fontWeight: '600',
                color: '#1f2937',
                margin: 0
              }}>
                Processing Queue
              </h3>
              <div style={{ display: 'flex', gap: '12px' }}>
                <select
                  value={filterStatus}
                  onChange={(e) => setFilterStatus(e.target.value)}
                  style={{
                    padding: '8px 12px',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    fontSize: '14px',
                    fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif'
                  }}
                >
                  <option value="all">All Status</option>
                  <option value="Processing">Processing</option>
                  <option value="Processed">Processed</option>
                  <option value="Error">Error</option>
                </select>
                <select
                  value={filterType}
                  onChange={(e) => setFilterType(e.target.value)}
                  style={{
                    padding: '8px 12px',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    fontSize: '14px',
                    fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif'
                  }}
                >
                  <option value="all">All Types</option>
                  <option value="PDF">PDF</option>
                  <option value="Word">Word</option>
                  <option value="Excel">Excel</option>
                </select>
              </div>
            </div>

            {/* Search Bar */}
            <div style={{ position: 'relative', marginBottom: '20px' }}>
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search documents..."
                style={{
                  width: '100%',
                  padding: '12px 16px 12px 48px',
                  border: '1px solid #d1d5db',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif'
                }}
              />
              <Search size={20} style={{
                position: 'absolute',
                left: '16px',
                top: '50%',
                transform: 'translateY(-50%)',
                color: '#9ca3af'
              }} />
            </div>
          </div>

          {/* Documents DataTable */}
          <div className="page-table-container">
            <DataTable
              data={filteredDocuments}
              columns={columns}
              title="Document Ingestion"
              searchable={true}
              sortable={true}
              exportable={true}
              pagination={true}
              pageSize={10}
              showActions={true}
              showCheckboxes={false}
              onView={handleView}
              onEdit={handleDownload}
              onDelete={handleDelete}
              searchPlaceholder="Search documents..."
              emptyMessage="No documents found. Upload documents to get started."
              initialHiddenColumns={['id']}
            />
          </div>
        </div>
      )}

      {/* Extracted Data Tab */}
      {activeTab === 'extracted' && (
        <div>
          {/* Extracted Data Overview */}
          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: '24px',
            marginBottom: '24px',
            boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
            border: '1px solid #e5e7eb'
          }}>
            <h3 style={{
              fontSize: '18px',
              fontWeight: '600',
              color: '#1f2937',
              margin: '0 0 20px 0',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              <FileCheck size={20} style={{ color: 'var(--primary-color)' }} />
              Extracted Data
            </h3>
            
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
              gap: '20px'
            }}>
              {documentData.filter(doc => doc.status === 'Processed' && doc.extractedData).map((doc) => (
                <div key={doc.id} style={{
                  padding: '20px',
                  backgroundColor: '#f8fafc',
                  borderRadius: '8px',
                  border: '1px solid #e5e7eb'
                }}>
                  <h4 style={{
                    fontSize: '16px',
                    fontWeight: '600',
                    color: '#1f2937',
                    margin: '0 0 12px 0'
                  }}>
                    {doc.originalName}
                  </h4>
                  
                  <div style={{ marginBottom: '16px' }}>
                    <h5 style={{
                      fontSize: '14px',
                      fontWeight: '500',
                      color: '#374151',
                      margin: '0 0 8px 0'
                    }}>
                      Extracted Information:
                    </h5>
                    <div style={{ fontSize: '14px', color: '#6b7280' }}>
                      <div><strong>Type:</strong> {doc.extractedData.contractType}</div>
                      <div><strong>Parties:</strong> {doc.extractedData.parties?.join(', ')}</div>
                      <div><strong>Start Date:</strong> {doc.extractedData.startDate}</div>
                      <div><strong>End Date:</strong> {doc.extractedData.endDate}</div>
                      <div><strong>Value:</strong> {doc.extractedData.value}</div>
                    </div>
                  </div>

                  <div>
                    <h5 style={{
                      fontSize: '14px',
                      fontWeight: '500',
                      color: '#374151',
                      margin: '0 0 8px 0'
                    }}>
                      Key Terms:
                    </h5>
                    <div style={{ fontSize: '14px', color: '#6b7280' }}>
                      {doc.extractedData.keyTerms?.map((term, index) => (
                        <span key={index} style={{
                          display: 'inline-block',
                          padding: '2px 8px',
                          backgroundColor: '#e5e7eb',
                          borderRadius: '4px',
                          margin: '2px',
                          fontSize: '12px'
                        }}>
                          {term}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Analytics Tab */}
      {activeTab === 'analytics' && (
        <div style={{
          backgroundColor: 'white',
          borderRadius: '12px',
          padding: '20px',
          boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
          border: '1px solid #e5e7eb',
          minHeight: '300px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: '18px',
          color: '#6b7280'
        }}>
          Document Ingestion Analytics Dashboard Coming Soon!
        </div>
      )}

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={handleDeleteConfirm}
        title="Delete Document"
        message={`Are you sure you want to delete "${selectedItem?.originalName}"? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        type="danger"
        isLoading={isDeleting}
      />

      <style jsx>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default DocumentIngestion;
